/*
 * Packungo Direktbestellung
 * Größe + Produktionsmenge wählen die passende Shopify-Variante. Der Preis kommt nur aus der Variante.
 * Keine Preise für Mengen, die es nicht als Variante gibt: Dann werden die nächsten Stufen angeboten.
 * Absenden wie das Theme (product-form): FormData an /cart/add, danach Warenkorb-Drawer öffnen.
 */
if (!customElements.get('direct-order-form')) {
  class DirectOrderForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.variants = this.readJson('[data-direct-order-variants]', []).map((v) => ({
        ...v,
        qty: parseInt(String(v.qtyLabel || '').replace(/\D/g, ''), 10) || 0,
      }));
      this.specs = this.readJson('[data-direct-order-specs]', {}) || {};
      this.maxFileBytes = (parseFloat(this.dataset.maxFileMb) || 20) * 1024 * 1024;
      this.currency = this.dataset.currency || 'EUR';
      this.vatRate = (parseFloat(this.dataset.vatRate) || 19) / 100;

      this.form = this.querySelector('form');
      this.sizeInputs = [...this.querySelectorAll('[data-do-size]')];
      this.qtyInput = this.querySelector('[data-do-qty]');
      this.quickWrap = this.querySelector('[data-do-quick]');
      this.qtyHint = this.querySelector('[data-do-qty-hint]');
      this.variantInput = this.querySelector('[data-do-variant-input]');
      this.propQty = this.querySelector('[data-do-prop-qty]');
      this.totalEl = this.querySelector('[data-do-total]');
      this.lineLabel = this.querySelector('[data-do-line-label]');
      this.lineTotal = this.querySelector('[data-do-line-total]');
      this.vatEl = this.querySelector('[data-do-vat]');
      this.grossEl = this.querySelector('[data-do-gross]');
      this.unitEl = this.querySelector('[data-do-unit]');
      this.sizeLabel = this.querySelector('[data-do-size-label]');
      this.specsEl = this.querySelector('[data-do-specs]');
      this.errorEl = this.querySelector('[data-do-error]');
      this.submitBtn = this.querySelector('[data-do-submit]');
      this.checkoutBtn = this.querySelector('[data-do-checkout]');
      this.artInputs = [...this.querySelectorAll('[data-do-art]')];
      this.sticky = this.querySelector('[data-do-sticky]');

      this.size = null;
      this.qty = null;
      this.variant = null;

      this.sizeInputs.forEach((input) => input.addEventListener('change', () => this.setSize(input.value)));
      this.qtyInput.addEventListener('input', () => this.setQty(this.parseQty(this.qtyInput.value), false));
      this.qtyInput.addEventListener('blur', () => {
        if (this.qty) this.qtyInput.value = this.formatQty(this.qty);
      });
      this.qtyInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
      this.artInputs.forEach((input) => input.addEventListener('change', () => this.setArtwork(input)));
      this.querySelectorAll('[data-do-step]').forEach((b) => b.addEventListener('click', () => this.step(parseInt(b.dataset.doStep, 10))));
      this.querySelectorAll('[data-do-file]').forEach((wrap) => this.initFile(wrap));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit(false, e.submitter || this.submitBtn);
      });
      this.checkoutBtn?.addEventListener('click', () => this.submit(true, this.checkoutBtn));
      this.querySelector('[data-do-sticky-button]')?.addEventListener('click', () => this.submit(false, this.submitBtn));

      this.initialSelection();
      this.initSticky();
    }

    readJson(selector, fallback) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '') ?? fallback;
      } catch (e) {
        return fallback;
      }
    }

    /* ---------- Formatierung ---------- */
    money(amount, digits = 2) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount);
    }
    formatQty(n) {
      return new Intl.NumberFormat('de-DE').format(n);
    }
    parseQty(text) {
      const digits = String(text || '').replace(/\D/g, '');
      return digits ? parseInt(digits, 10) : null;
    }

    /* ---------- Auswahl ---------- */
    tiersFor(size) {
      return [...new Set(this.variants.filter((v) => v.size === size).map((v) => v.qty))].sort((a, b) => a - b);
    }
    allTiers() {
      return [...new Set(this.variants.map((v) => v.qty))].sort((a, b) => a - b);
    }

    initialSelection() {
      const fromUrl = this.variants.find((v) => String(v.id) === this.dataset.selectedVariant);
      const sizes = this.sizeInputs.map((i) => i.value);
      let size = fromUrl?.size || (sizes.includes(this.dataset.defaultSize) ? this.dataset.defaultSize : null);
      if (!size && sizes.length) size = sizes[Math.floor((sizes.length - 1) / 2)];
      let qty = fromUrl?.qty || parseInt(this.dataset.defaultQuantity, 10) || null;
      if (size && qty && !this.tiersFor(size).includes(qty)) qty = this.tiersFor(size)[0] || qty;
      this.qty = qty;
      if (qty) this.qtyInput.value = this.formatQty(qty);
      if (size) {
        const input = this.sizeInputs.find((i) => i.value === size);
        if (input) input.checked = true;
        this.setSize(size, false);
      } else {
        this.renderQuick();
        this.update(false);
      }
    }

    setSize(size, updateUrl = true) {
      this.size = size;
      if (this.sizeLabel) this.sizeLabel.textContent = size || '';
      this.renderSpecs();
      this.renderQuick();
      this.update(updateUrl);
    }

    /** −/+ springt zur vorherigen bzw. nächsten Produktionsmenge. */
    step(dir) {
      const tiers = this.size ? this.tiersFor(this.size) : this.allTiers();
      if (!tiers.length) return;
      const q = this.qty || 0;
      const next = dir > 0 ? tiers.find((t) => t > q) ?? tiers[tiers.length - 1] : [...tiers].reverse().find((t) => t < q) ?? tiers[0];
      this.setQty(next);
    }

    setQty(qty, syncInput = true) {
      this.qty = qty;
      if (syncInput) this.qtyInput.value = qty ? this.formatQty(qty) : '';
      this.renderQuick();
      this.update(true);
    }

    renderSpecs() {
      const s = this.size && this.specs[this.size];
      if (!s) {
        this.specsEl.hidden = true;
        return;
      }
      const parts = [];
      if (s.hoehe) parts.push(`Höhe ${s.hoehe} mm`);
      if (s.oben) parts.push(`Ø oben ${s.oben} mm`);
      if (s.unten) parts.push(`Ø unten ${s.unten} mm`);
      if (s.fuellmenge) parts.push(`max. Füllmenge ${s.fuellmenge} ml`);
      this.specsEl.textContent = parts.join(' · ');
      this.specsEl.hidden = parts.length === 0;
    }

    renderQuick() {
      const tiers = this.size ? this.tiersFor(this.size) : this.allTiers();
      const wanted = (this.dataset.quickQuantities || '')
        .split(',')
        .map((x) => parseInt(x.replace(/\D/g, ''), 10))
        .filter(Boolean);
      const list = (wanted.length ? wanted : tiers).filter((q) => tiers.includes(q));
      this.quickWrap.innerHTML = '';
      list.forEach((q) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'direct-order__chip';
        b.textContent = this.formatQty(q);
        b.setAttribute('aria-pressed', String(q === this.qty));
        b.addEventListener('click', () => this.setQty(q));
        this.quickWrap.appendChild(b);
      });
    }

    /** Zeigt passende Produktionsmengen, ohne die Eingabe zu ändern. */
    renderQtyHint(state) {
      const h = this.qtyHint;
      h.innerHTML = '';
      h.className = 'direct-order__hint text-sm';
      if (!state) return;
      const text = document.createElement('p');
      text.textContent = state.text;
      h.appendChild(text);
      if (state.options?.length) {
        const wrap = document.createElement('div');
        wrap.className = 'direct-order__hint-options flex flex-wrap gap-2';
        state.options.forEach((q) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'direct-order__pill';
          b.textContent = `${this.formatQty(q)} Stück`;
          b.addEventListener('click', () => this.setQty(q));
          wrap.appendChild(b);
        });
        h.appendChild(wrap);
      }
      if (state.link) {
        const a = document.createElement('a');
        a.href = this.dataset.anfrageUrl;
        a.className = 'link';
        a.textContent = state.link;
        h.appendChild(a);
      }
      if (state.tone) h.classList.add(`direct-order__hint--${state.tone}`);
    }

    qtyState() {
      if (!this.size) return null;
      const tiers = this.tiersFor(this.size);
      const min = tiers[0];
      const max = tiers[tiers.length - 1];
      const q = this.qty;
      if (!q) return { text: `Bitte geben Sie eine Menge ein (ab ${this.formatQty(min)} Stück).`, tone: 'info' };
      if (tiers.includes(q)) return null;
      if (q < min) return { text: `Die Mindestmenge für diese Größe beträgt ${this.formatQty(min)} Stück.`, options: [min], tone: 'warning' };
      if (q > max)
        return {
          text: `Für mehr als ${this.formatQty(max)} Stück erstellen wir Ihnen gern ein individuelles Angebot.`,
          options: [max],
          link: 'Angebot anfragen',
          tone: 'warning',
        };
      const lower = [...tiers].reverse().find((t) => t < q);
      const upper = tiers.find((t) => t > q);
      return {
        text: `Verfügbare Produktionsmengen: ${this.formatQty(lower)} oder ${this.formatQty(upper)} Stück`,
        options: [lower, upper],
        tone: 'warning',
      };
    }

    update(updateUrl) {
      const variant =
        this.size && this.qty ? this.variants.find((v) => v.size === this.size && v.qty === this.qty) : null;
      this.variant = variant || null;
      this.renderQtyHint(this.qtyState());

      const ok = Boolean(variant && variant.available);
      this.variantInput.value = ok ? variant.id : '';
      this.variantInput.disabled = !ok;
      this.propQty.value = ok ? String(variant.qty) : '';

      if (variant && !variant.available) {
        this.renderQtyHint({ text: 'Diese Kombination ist derzeit nicht bestellbar.', tone: 'warning' });
      }

      const set = (el, text) => el && (el.textContent = text);
      if (ok) {
        const total = variant.price / 100;
        const unit = total / variant.qty;
        const vat = Math.round(total * this.vatRate * 100) / 100;
        set(this.totalEl, this.money(total));
        set(this.unitEl, this.money(unit, 3));
        set(this.lineLabel, `${this.formatQty(variant.qty)} × ${this.money(unit, 3)} (inkl. Druck)`);
        set(this.lineTotal, this.money(total));
        set(this.vatEl, this.money(vat));
        set(this.grossEl, this.money(total + vat));
      } else {
        [this.totalEl, this.unitEl, this.lineTotal, this.vatEl, this.grossEl].forEach((el) => set(el, '–'));
        set(this.lineLabel, 'Größe und Menge wählen');
      }
      this.classList.toggle('direct-order--ready', ok);
      this.updateSticky();
      this.clearError();

      if (updateUrl && ok && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /* ---------- Druckdaten ---------- */
    setArtwork(active) {
      this.artInputs.forEach((input) => {
        const choice = input.closest('[data-do-choice]');
        const panel = choice.querySelector('[data-do-panel]');
        const on = input === active && input.checked;
        panel.hidden = !on;
        choice.classList.toggle('is-active', on);
        panel.querySelectorAll('input, textarea').forEach((el) => (el.disabled = !on));
      });
      this.clearError();
    }

    initFile(wrap) {
      const input = wrap.querySelector('input[type=file]');
      const name = wrap.querySelector('[data-do-file-name]');
      const meta = wrap.querySelector('[data-do-file-meta]');
      const remove = wrap.querySelector('[data-do-file-remove]');
      const drop = wrap.querySelector('[data-do-file-drop]');
      const defaults = { name: name.textContent, meta: meta.textContent };
      const render = () => {
        const f = input.files?.[0];
        wrap.classList.toggle('has-file', Boolean(f));
        wrap.classList.remove('has-error');
        name.textContent = f ? f.name : defaults.name;
        meta.textContent = f ? `${(f.size / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : defaults.meta;
        remove.hidden = !f;
        if (f) {
          const problem = this.fileProblem(f);
          if (problem) {
            wrap.classList.add('has-error');
            meta.textContent = problem;
          }
        }
        this.clearError();
      };
      input.addEventListener('change', render);
      remove.addEventListener('click', () => {
        input.value = '';
        render();
      });
      ['dragover', 'dragenter'].forEach((t) =>
        drop.addEventListener(t, (e) => {
          if (input.disabled) return;
          e.preventDefault();
          drop.classList.add('is-dragover');
        })
      );
      ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, () => drop.classList.remove('is-dragover')));
      drop.addEventListener('drop', (e) => {
        if (input.disabled || !e.dataTransfer?.files?.length) return;
        e.preventDefault();
        const dt = new DataTransfer();
        dt.items.add(e.dataTransfer.files[0]);
        input.files = dt.files;
        render();
      });
    }

    fileProblem(file) {
      const ok = /\.(pdf|svg|png|jpe?g)$/i.test(file.name);
      if (!ok) return 'Dateiformat nicht unterstützt. Bitte PDF, SVG, PNG oder JPG verwenden.';
      if (file.size > this.maxFileBytes)
        return `Die Datei ist zu groß (max. ${Math.round(this.maxFileBytes / 1024 / 1024)} MB).`;
      return null;
    }

    /* ---------- Prüfen und Absenden ---------- */
    validate() {
      if (!this.size) return this.fail('Bitte wählen Sie eine Größe.', this.sizeInputs[0]);
      if (!this.qty) return this.fail('Bitte geben Sie die gewünschte Stückzahl ein.', this.qtyInput);
      if (!this.variant) {
        const s = this.qtyState();
        return this.fail(s ? s.text : 'Für diese Menge gibt es keinen Festpreis.', this.qtyInput);
      }
      if (!this.variant.available) return this.fail('Diese Kombination ist derzeit nicht bestellbar.', this.qtyInput);
      const art = this.artInputs.find((i) => i.checked);
      if (!art) return this.fail('Bitte wählen Sie: Logo/Design jetzt hochladen oder später senden.', this.artInputs[0]);
      // Später senden: keine Datei nötig, direkt bestellbar.
      if (art.closest('[data-do-choice]').dataset.doChoice === 'later') return true;
      const panel = art.closest('[data-do-choice]').querySelector('[data-do-panel]');
      const file = panel.querySelector('input[type=file]');
      const f = file?.files?.[0];
      if (!f) return this.fail('Bitte laden Sie Ihr Logo oder Ihr Design hoch – oder wählen Sie „Später senden“.', file);
      const problem = this.fileProblem(f);
      if (problem) return this.fail(problem, file);
      return true;
    }

    fail(message, focusEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      const target = focusEl?.type === 'file' ? focusEl.closest('[data-do-file]') : focusEl;
      (target || this.errorEl).scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (focusEl && focusEl.type !== 'file' && focusEl.type !== 'radio') focusEl.focus({ preventScroll: true });
      return false;
    }

    clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    setLoading(button, on) {
      [this.submitBtn, this.checkoutBtn].forEach((b) => b && b.setAttribute('aria-disabled', String(on)));
      button?.classList.toggle('btn--loading', on);
    }

    async submit(goCheckout, button) {
      if (this.busy) return;
      this.clearError();
      if (!this.validate()) return;

      const routes = window.FoxTheme?.routes || {};
      const formData = new FormData(this.form);
      // Leere Textfelder nicht als Eigenschaft mitsenden.
      for (const [key, value] of [...formData.entries()]) {
        if (key.startsWith('properties[') && typeof value === 'string' && !value.trim()) formData.delete(key);
      }
      // Sicherheitsnetz: Variante muss zur Anzeige passen
      if (String(formData.get('id')) !== String(this.variant.id)) {
        this.fail('Die Auswahl konnte nicht übernommen werden. Bitte laden Sie die Seite neu.');
        return;
      }
      const useDrawer = !goCheckout && window.FoxTheme?.settings?.cartType !== 'page' && document.querySelector('cart-drawer');
      if (useDrawer) {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
      }

      this.busy = true;
      this.setLoading(button, true);
      try {
        const response = await fetch(routes.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        let state = {};
        try {
          state = await response.json();
        } catch (e) {
          state = { status: response.status };
        }
        if (!response.ok || state.status) {
          const fileIssue = response.status === 413 || /file|datei|upload/i.test(`${state.description || ''}${state.message || ''}`);
          this.fail(
            fileIssue
              ? 'Die Datei konnte nicht hochgeladen werden. Bitte prüfen Sie Format und Größe oder senden Sie die Datei nach der Bestellung per E-Mail.'
              : state.description || 'Der Artikel konnte nicht in den Warenkorb gelegt werden. Bitte versuchen Sie es erneut.'
          );
          return;
        }

        if (goCheckout) {
          window.location.href = `${(routes.root_url || '/').replace(/\/$/, '')}/checkout`;
          return;
        }

        const cart = await (
          await fetch(routes.cart_url || '/cart', { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } })
        ).json();
        cart.sections = state.sections;
        if (window.FoxTheme?.pubsub) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        }
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: state } }));
        const drawer = document.querySelector('cart-drawer');
        if (useDrawer && drawer?.show) drawer.show(button);
        else window.location.href = routes.cart_url || '/cart';
      } catch (error) {
        this.fail('Verbindung fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.');
      } finally {
        this.busy = false;
        this.setLoading(button, false);
      }
    }

    /* ---------- Mobile Leiste ---------- */
    initSticky() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;
      const actions = this.querySelector('[data-do-actions]');
      this.actionsVisible = true;
      new IntersectionObserver((entries) => {
        this.actionsVisible = entries[0].isIntersecting;
        this.updateSticky();
      }).observe(actions);
      this.updateSticky();
    }

    updateSticky() {
      if (!this.sticky) return;
      const total = this.sticky.querySelector('[data-do-sticky-total]');
      const detail = this.sticky.querySelector('[data-do-sticky-detail]');
      if (this.variant && this.variant.available) {
        total.textContent = `${this.money(this.variant.price / 100)} netto`;
        detail.textContent = `${this.size} · ${this.formatQty(this.variant.qty)} Stück`;
      } else {
        total.textContent = 'Größe und Menge wählen';
        detail.textContent = '';
      }
      const show = this.actionsVisible === false;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', String(!show));
      this.sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
    }
  }

  customElements.define('direct-order-form', DirectOrderForm);
}
